/**
 * support-api.ts — Support ticket API.
 *
 * User-facing:  POST/GET /support/tickets
 * Superadmin:   GET /admin/support/tickets, PATCH /admin/support/tickets/{id}
 */
import { apiClient } from './index';

export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high';
export type TicketCategory = 'question' | 'bug' | 'feature' | 'billing' | 'other';

export interface SupportTicket {
    id: string;
    tenant_id?: string | null;
    user_id: string;
    user_email: string;
    subject: string;
    body: string;
    category: TicketCategory;
    status: TicketStatus;
    priority: TicketPriority;
    admin_notes?: string | null;
    created_at: string;
    updated_at: string;
}

export interface CreateTicketInput {
    subject: string;
    body: string;
    category: TicketCategory;
}

/**
 * What a ticket's author may see (backend support.CustomerTicket): no
 * admin_notes. The backend already omits it (SUP-01); dropping it here too
 * means a regression there still can't put an operator's note on screen.
 */
export type CustomerTicket = Omit<SupportTicket, 'admin_notes'>;

function customerView({ admin_notes: _internal, ...t }: SupportTicket): CustomerTicket {
    return t;
}

export async function createTicket(input: CreateTicketInput): Promise<CustomerTicket> {
    const { data } = await apiClient.post<SupportTicket>('/support/tickets', input);
    return customerView(data);
}

export async function listMyTickets(): Promise<CustomerTicket[]> {
    const { data } = await apiClient.get<{ data: SupportTicket[] | null }>('/support/tickets');
    return (data.data ?? []).map(customerView);
}

/** Server-side filters for the operator inbox; empty values do not filter. */
export interface AdminListParams {
    status?: TicketStatus | '';
    priority?: TicketPriority | '';
    category?: TicketCategory | '';
    tenant_id?: string;
    /** Case-insensitive substring of the requester's email. */
    requester?: string;
    page?: number;
    limit?: number;
}

export async function adminListTickets(params: AdminListParams = {}): Promise<{ data: SupportTicket[]; total: number }> {
    const { data } = await apiClient.get<{ data: SupportTicket[]; meta: { total: number } }>(
        '/admin/support/tickets',
        { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== undefined)) },
    );
    return { data: data.data ?? [], total: data.meta?.total ?? 0 };
}

export interface AdminUpdateInput {
    status?: TicketStatus;
    priority?: TicketPriority;
    admin_notes?: string;
}

export async function adminUpdateTicket(id: string, input: AdminUpdateInput): Promise<SupportTicket> {
    const { data } = await apiClient.patch<SupportTicket>(`/admin/support/tickets/${id}`, input);
    return data;
}
